<template>
  <div v-if="chartData?.chart?.data" class="chart-container">
    <div v-if="chartData.chart.type === 'pie'" class="pie-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div v-else-if="chartData.chart.type === 'bar'" class="bar-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div v-else-if="chartData.chart.type === 'line'" class="line-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div v-else-if="chartData.chart.type === 'area'" class="area-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div v-else-if="chartData.chart.type === 'radar'" class="radar-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div v-else-if="chartData.chart.type === 'scatter'" class="scatter-chart">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, watch, defineProps} from 'vue';
import Chart from 'chart.js/auto';

const props = defineProps({
  chartData: {
    type: Object,
    required: true
  }
});

const chartId = 'chart-' + Math.random().toString(36).substr(2, 9);
const chartCanvas = ref(null);
let chartInstance = null;

const COLORS = [
  'rgba(136, 132, 216, 0.8)',
  'rgba(130, 202, 157, 0.8)',
  'rgba(255, 198, 88, 0.8)',
  'rgba(255, 115, 0, 0.8)',
  'rgba(0, 136, 254, 0.8)',
  'rgba(0, 196, 159, 0.8)',
  'rgba(255, 128, 66, 0.8)',
  'rgba(156, 39, 176, 0.8)'
];

const createPieChart = (data) => {
  return {
    type: 'pie',
    data: {
      labels: data.map(item => item.label),
      datasets: [{
        data: data.map(item => item.value),
        backgroundColor: COLORS,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createBarChart = (data) => {
  return {
    type: 'bar',
    data: {
      labels: data.map(item => item.label),
      datasets: [{
        label: 'Value',
        data: data.map(item => item.value),
        backgroundColor: COLORS[0],
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        },
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createLineChart = (data) => {
  return {
    type: 'line',
    data: {
      labels: data.map(item => item.label),
      datasets: [{
        label: 'Value',
        data: data.map(item => item.value),
        borderColor: COLORS[0],
        tension: 0.4,
        fill: false
      }]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        },
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createAreaChart = (data) => {
  return {
    type: 'line',
    data: {
      labels: data.map(item => item.label),
      datasets: [{
        label: 'Value',
        data: data.map(item => item.value),
        borderColor: COLORS[0],
        backgroundColor: `${COLORS[0]}44`,
        fill: true,
        tension: 0.4
      }]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        },
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createRadarChart = (data) => {
  return {
    type: 'radar',
    data: {
      labels: data.map(item => item.label),
      datasets: [{
        label: 'Value',
        data: data.map(item => item.value),
        backgroundColor: `${COLORS[0]}44`,
        borderColor: COLORS[0],
        borderWidth: 2,
        pointBackgroundColor: COLORS[0]
      }]
    },
    options: {
      responsive: true,
      scales: {
        r: {
          angleLines: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          pointLabels: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createScatterChart = (data) => {
  return {
    type: 'scatter',
    data: {
      datasets: [{
        label: 'Value',
        data: data.map(item => ({
          x: item.label,
          y: item.value
        })),
        backgroundColor: COLORS[0]
      }]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        },
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#999'
          }
        }
      }
    }
  };
};

const createChart = () => {
  if (!props.chartData?.chart?.data || !chartCanvas.value) return;

  const {type, data} = props.chartData.chart;
  let config;

  switch (type.toLowerCase()) {
    case 'pie':
      config = createPieChart(data);
      break;
    case 'bar':
      config = createBarChart(data);
      break;
    case 'line':
      config = createLineChart(data);
      break;
    case 'area':
      config = createAreaChart(data);
      break;
    case 'radar':
      config = createRadarChart(data);
      break;
    case 'scatter':
      config = createScatterChart(data);
      break;
    default:
      return;
  }

  if (chartInstance) {
    chartInstance.destroy();
  }

  chartInstance = new Chart(chartCanvas.value, config);
};

watch(
    () => props.chartData,
    () => {
      createChart();
    },
    {deep: true}
);

onMounted(() => {
  createChart();
});
</script>

<style scoped>
.chart-container {
  margin: 16px 0;
  padding: 16px;
  background: var(--white-opacity-4);
  border-radius: 8px;
  border: 1px solid var(--white-opacity-8);
  max-height: 400px;
  position: relative;
}

.pie-chart,
.bar-chart,
.line-chart,
.area-chart,
.radar-chart,
.scatter-chart {
  max-height: 400px;
  width: 100%;
}
</style>